/**
 * Manage Crew → Clients tab.
 *
 * Lists the registry, soft-deactivates clients (drops them from pickers),
 * and onboards new client orgs with optional seed users. Visible when
 * VITE_MULTI_CLIENT=on and the caller can manage:users.
 */

import { useCallback, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Trash2, X } from "lucide-react";
import {
  createClient,
  listClients,
  patchClientStatus,
  type ClientEntry,
  type OnboardCredential,
  type OnboardUserSpec,
} from "@/lib/docs-api";
import { isMultiClientEnabled, isZitadelMode } from "@/env";
import { can } from "@/lib/auth/can";
import { useAuth } from "@/hooks/use-auth";
import {
  addSeedClient,
  listSeedClientEntries,
  setSeedClientStatus,
} from "@/lib/seed-clients";

const CLIENT_ROLES = [
  "MISSION_DIRECTOR",
  "DATA_ANALYST",
  "SYSTEMS_ENGINEER",
  "MISSION_OBSERVER",
] as const;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);
}

/** Console chrome — matches Meet Crew / Power User (DXC tokens, no glass). */
const chrome = {
  input: {
    background: "rgba(var(--accent-true-blue-rgb), 0.06)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
    color: "var(--text-primary)",
  } as const,
  panel: {
    background: "var(--bg-panel-alt)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
  } as const,
  row: {
    background: "rgba(var(--accent-true-blue-rgb), 0.04)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
  } as const,
  rowInactive: {
    background: "transparent",
    border: "1px solid transparent",
  } as const,
  cta: {
    color: "var(--accent-sky)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
    background: "rgba(var(--accent-true-blue-rgb), 0.1)",
    boxShadow: "0 0 12px rgba(var(--accent-true-blue-rgb), 0.1)",
  } as const,
};

export function ClientsManageBody() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const visible = isMultiClientEnabled() && can(user, "manage:users");
  const zitadel = isZitadelMode();

  const [seedTick, setSeedTick] = useState(0);
  const clientsQuery = useQuery({
    queryKey: ["clients"],
    queryFn: listClients,
    enabled: visible && zitadel,
  });

  // seedTick bumps after localStorage mutates so seed mode re-reads.
  const clients: ClientEntry[] = zitadel
    ? (clientsQuery.data ?? [])
    : (seedTick, listSeedClientEntries());

  const [name, setName] = useState("");
  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [users, setUsers] = useState<OnboardUserSpec[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<OnboardCredential[] | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (zitadel) {
      await queryClient.invalidateQueries({ queryKey: ["clients"] });
    } else {
      setSeedTick((n) => n + 1);
    }
  }, [queryClient, zitadel]);

  if (!visible) return null;

  function onNameChange(value: string) {
    setName(value);
    if (!idTouched) setId(slugify(value));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (zitadel) {
        const result = await createClient({
          id: id.trim(),
          name: name.trim(),
          users: users
            .filter((u) => u.email.trim() && u.givenName.trim())
            .map((u) => ({
              email: u.email.trim(),
              givenName: u.givenName.trim(),
              familyName: (u.familyName ?? "").trim() || undefined,
              role: u.role,
            })),
        });
        setCredentials(result.credentials ?? []);
      } else {
        addSeedClient({ id: id.trim(), name: name.trim() });
      }
      setName("");
      setId("");
      setIdTouched(false);
      setUsers([]);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onDeactivate(clientId: string) {
    setRemoveBusy(true);
    setError(null);
    try {
      if (zitadel) {
        await patchClientStatus(clientId, "inactive");
      } else {
        setSeedClientStatus(clientId, "inactive");
      }
      setConfirmRemove(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setRemoveBusy(false);
    }
  }

  async function onReactivate(clientId: string) {
    setError(null);
    try {
      if (zitadel) {
        await patchClientStatus(clientId, "active");
      } else {
        setSeedClientStatus(clientId, "active");
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="flex max-h-[70vh] flex-col gap-2">
      <div className="flex flex-shrink-0 items-baseline gap-2">
        <h3
          className="font-mono text-fs-label tracking-widest uppercase"
          style={{ color: "var(--text-primary)" }}
        >
          Clients
        </h3>
        <span
          className="font-mono text-fs-micro tracking-widest"
          style={{ color: "var(--text-muted)" }}
        >
          {clients.length}
        </span>
      </div>

      <ul className="flex flex-col gap-0.5" aria-label="Registered clients">
        {clients.length === 0 && (
          <li
            className="rounded-xl py-2 text-center font-mono text-fs-micro tracking-widest"
            style={{ ...chrome.panel, color: "var(--text-muted)" }}
          >
            No clients. Add one below.
          </li>
        )}
        {clients.map((client) => {
          const inactive = client.status !== "active";
          const protectedId = client.id === "dxc" || client.id === "local";
          const confirming = confirmRemove === client.id;
          return (
            <li
              key={client.id}
              className="rounded-xl px-2.5 py-1.5"
              style={{
                ...(inactive ? chrome.rowInactive : chrome.row),
                opacity: inactive ? 0.55 : 1,
              }}
            >
              <div className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate font-mono text-fs-label tracking-widest"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {client.name}
                  </p>
                  <p
                    className="truncate font-mono text-fs-micro tracking-widest"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {client.id}
                  </p>
                </div>
                {!protectedId && !inactive && (
                  <button
                    type="button"
                    aria-expanded={confirming}
                    aria-label={`Remove ${client.name}`}
                    onClick={() =>
                      setConfirmRemove(confirming ? null : client.id)
                    }
                    className="rounded border p-1.5 transition-colors"
                    style={{
                      borderColor: "rgba(var(--accent-true-blue-rgb), 0.2)",
                      background: "transparent",
                    }}
                  >
                    <Trash2
                      className="h-3.5 w-3.5"
                      style={{ color: "var(--accent-red)" }}
                    />
                  </button>
                )}
                {!protectedId && inactive && (
                  <button
                    type="button"
                    onClick={() => void onReactivate(client.id)}
                    className="rounded-sm border px-2 py-1 font-mono text-fs-micro tracking-widest"
                    style={{
                      borderColor: "rgba(var(--accent-true-blue-rgb), 0.4)",
                      color: "var(--accent-true-blue)",
                      background: "rgba(var(--accent-true-blue-rgb), 0.06)",
                    }}
                  >
                    Reactivate
                  </button>
                )}
              </div>
              {confirming && (
                <div
                  className="mt-1.5 space-y-1.5 border-t pt-1.5"
                  style={{ borderColor: "rgba(var(--accent-true-blue-rgb), 0.12)" }}
                >
                  <p
                    className="font-mono text-fs-micro tracking-widest"
                    style={{ color: "var(--text-muted)" }}
                  >
                    Remove from picker. Blobs stay.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={removeBusy}
                      onClick={() => void onDeactivate(client.id)}
                      className="rounded-sm border px-2.5 py-1 font-mono text-fs-micro tracking-widest disabled:opacity-40"
                      style={{
                        borderColor: "var(--accent-red)",
                        color: "var(--accent-red)",
                        background: "rgba(var(--accent-red-rgb, 220, 38, 38), 0.08)",
                      }}
                    >
                      {removeBusy ? "Removing…" : "Confirm remove"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmRemove(null)}
                      className="rounded-sm border px-2.5 py-1 font-mono text-fs-micro tracking-widest"
                      style={{
                        borderColor: "rgba(var(--accent-true-blue-rgb), 0.2)",
                        color: "var(--text-muted)",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <form
        onSubmit={(e) => void onSubmit(e)}
        className="space-y-2 rounded-xl p-2.5"
        style={chrome.panel}
        aria-label="Add client"
      >
        <p
          className="font-mono text-fs-micro tracking-widest uppercase"
          style={{ color: "var(--text-muted)" }}
        >
          Add
        </p>

        <div className="flex flex-wrap gap-2">
          <label className="flex min-w-[12rem] flex-1 flex-col gap-1">
            <span
              className="font-mono tracking-widest uppercase"
              style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
            >
              Name
            </span>
            <input
              required
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              className="rounded border px-2.5 py-1.5 font-mono text-xs outline-none"
              style={chrome.input}
              placeholder="Acme Corp"
            />
          </label>
          <label className="flex min-w-[10rem] flex-1 flex-col gap-1">
            <span
              className="font-mono tracking-widest uppercase"
              style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
            >
              Id
            </span>
            <input
              required
              value={id}
              onChange={(e) => {
                setIdTouched(true);
                setId(e.target.value.toLowerCase());
              }}
              pattern="[a-z0-9][a-z0-9-]{1,31}"
              className="rounded border px-2.5 py-1.5 font-mono text-xs outline-none"
              style={chrome.input}
              placeholder="acme"
            />
          </label>
        </div>

        {zitadel && (
          <div className="space-y-2">
            {users.map((row, idx) => (
              <div
                key={idx}
                className="flex flex-wrap items-center gap-2"
              >
                <input
                  type="email"
                  value={row.email}
                  onChange={(e) =>
                    setUsers((list) =>
                      list.map((r, i) =>
                        i === idx ? { ...r, email: e.target.value } : r,
                      ),
                    )
                  }
                  placeholder="email@client.com"
                  className="min-w-[10rem] flex-[1.4] rounded border px-2.5 py-1.5 font-mono text-xs outline-none"
                  style={chrome.input}
                  autoComplete="off"
                />
                <input
                  value={row.givenName}
                  onChange={(e) =>
                    setUsers((list) =>
                      list.map((r, i) =>
                        i === idx ? { ...r, givenName: e.target.value } : r,
                      ),
                    )
                  }
                  placeholder="Given name"
                  className="min-w-[6rem] flex-1 rounded border px-2.5 py-1.5 font-mono text-xs outline-none"
                  style={chrome.input}
                />
                <input
                  value={row.familyName ?? ""}
                  onChange={(e) =>
                    setUsers((list) =>
                      list.map((r, i) =>
                        i === idx ? { ...r, familyName: e.target.value } : r,
                      ),
                    )
                  }
                  placeholder="Family name"
                  className="min-w-[6rem] flex-1 rounded border px-2.5 py-1.5 font-mono text-xs outline-none"
                  style={chrome.input}
                />
                <select
                  value={row.role}
                  onChange={(e) =>
                    setUsers((list) =>
                      list.map((r, i) =>
                        i === idx ? { ...r, role: e.target.value } : r,
                      ),
                    )
                  }
                  className="rounded border px-2 py-1.5 font-mono text-fs-label tracking-widest outline-none"
                  style={chrome.input}
                >
                  {CLIENT_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Remove user row"
                  onClick={() =>
                    setUsers((list) => list.filter((_, i) => i !== idx))
                  }
                  className="rounded border p-1.5"
                  style={{
                    borderColor: "rgba(var(--accent-true-blue-rgb), 0.2)",
                    background: "transparent",
                  }}
                >
                  <Trash2
                    className="h-3.5 w-3.5"
                    style={{ color: "var(--text-muted)" }}
                  />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setUsers((u) => [
                  ...u,
                  {
                    email: "",
                    givenName: "",
                    familyName: "",
                    role: "MISSION_DIRECTOR",
                  },
                ])
              }
              className="flex items-center gap-1 rounded border px-2 py-1 font-mono text-fs-micro tracking-widest uppercase"
              style={{
                borderColor: "rgba(var(--accent-true-blue-rgb), 0.2)",
                color: "var(--text-muted)",
                background: "transparent",
              }}
            >
              <Plus className="h-3 w-3" aria-hidden />
              Add seed user
            </button>
            <p
              className="font-mono text-fs-micro tracking-wide"
              style={{ color: "var(--text-muted)" }}
            >
              Use a real mailbox (not @dxc.com). Temp password shown once;
              Forgot password needs SMTP — see docs/runbooks/zitadel-smtp-password-reset.md
            </p>
          </div>
        )}

        {error && (
          <p
            className="font-mono text-xs"
            style={{ color: "var(--accent-red)" }}
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy || !name.trim() || !id.trim()}
          className="rounded border px-3 py-1.5 font-mono text-fs-label font-semibold tracking-[0.2em] uppercase disabled:opacity-40"
          style={chrome.cta}
        >
          {busy ? "…" : "ADD"}
        </button>
      </form>

      {credentials && (
        <CredentialsDialog
          credentials={credentials}
          onClose={() => setCredentials(null)}
        />
      )}
    </div>
  );
}

/** @deprecated Prefer {@link ClientsManageBody}; kept for existing imports. */
export function ClientOnboardSection() {
  return <ClientsManageBody />;
}

function CredentialsDialog({
  credentials,
  onClose,
}: {
  credentials: OnboardCredential[];
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Temporary credentials"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
    >
      <div
        className="w-full max-w-md rounded-lg border p-5"
        style={{
          background: "var(--bg-panel)",
          borderColor: "rgba(var(--accent-true-blue-rgb), 0.35)",
        }}
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <div>
            <h4
              className="font-mono font-semibold tracking-[0.2em] uppercase"
              style={{ color: "var(--text-primary)", fontSize: "var(--fs-base)" }}
            >
              Credentials
            </h4>
            <p
              className="mt-0.5 font-mono"
              style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
            >
              Copy once — user must change password on first login.
              Forgot password works after SMTP is configured.
            </p>
          </div>
          <button type="button" aria-label="Close" onClick={onClose}>
            <X className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
          </button>
        </div>
        {credentials.length === 0 ? (
          <p className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
            No new users.
          </p>
        ) : (
          <ul className="space-y-2">
            {credentials.map((c) => (
              <li
                key={c.email}
                className="flex items-center justify-between gap-2 rounded border px-3 py-2"
                style={{
                  background: "rgba(var(--accent-true-blue-rgb), 0.04)",
                  borderColor: "rgba(var(--accent-true-blue-rgb), 0.2)",
                }}
              >
                <div className="min-w-0 font-mono text-xs">
                  <div style={{ color: "var(--text-primary)" }} className="truncate">
                    {c.email}
                  </div>
                  <div style={{ color: "var(--text-muted)" }}>{c.password}</div>
                </div>
                <button
                  type="button"
                  aria-label={`Copy credentials for ${c.email}`}
                  onClick={() =>
                    void navigator.clipboard.writeText(`${c.email}  ${c.password}`)
                  }
                  className="rounded border p-1.5"
                  style={{
                    borderColor: "rgba(var(--accent-true-blue-rgb), 0.25)",
                    background: "transparent",
                  }}
                >
                  <Copy
                    className="h-3.5 w-3.5"
                    style={{ color: "var(--accent-true-blue)" }}
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded border px-3 py-1.5 font-mono text-fs-label tracking-[0.2em] uppercase"
          style={chrome.cta}
        >
          Done
        </button>
      </div>
    </div>
  );
}
